import { useState, useEffect, useMemo } from 'react'
import { collection, query, where, onSnapshot } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import { Booking } from '@/types'
import {
    TrendingUp,
    DollarSign,
    Calendar,
    CreditCard,
    Download,
    Filter
} from 'lucide-react'
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import toast from 'react-hot-toast'

type PeriodFilter = 'today' | 'week' | 'month' | 'custom'
type GatewayFilter = 'all' | 'mercadopago' | 'infinitypay' | 'pix_manual'
type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected'

export default function FinancialDashboardPage() {
    const { currentOrg } = useAuthStore()
    const [bookings, setBookings] = useState<Booking[]>([])
    const [loading, setLoading] = useState(true)

    // Filtros
    const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('today')
    const [gatewayFilter, setGatewayFilter] = useState<GatewayFilter>('all')
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
    const [customStartDate, setCustomStartDate] = useState<Date>(new Date())
    const [customEndDate, setCustomEndDate] = useState<Date>(new Date())

    // Buscar bookings
    useEffect(() => {
        if (!currentOrg) return

        const q = query(
            collection(db, 'bookings'),
            where('orgId', '==', currentOrg.id),
            where('status', 'in', ['PENDING_PAYMENT', 'CONFIRMED', 'CANCELED'])
        )

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Booking))
            // Filtrar apenas os que têm informação de pagamento
            const withPayment = data.filter(b => b.payment || b.totalPrice > 0)
            setBookings(withPayment)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [currentOrg?.id])

    // Calcular período baseado no filtro
    const dateRange = useMemo(() => {
        const now = new Date()
        switch (periodFilter) {
            case 'today':
                return { start: startOfDay(now), end: endOfDay(now) }
            case 'week':
                return { start: startOfWeek(now, { locale: ptBR }), end: endOfWeek(now, { locale: ptBR }) }
            case 'month':
                return { start: startOfMonth(now), end: endOfMonth(now) }
            case 'custom':
                return { start: startOfDay(customStartDate), end: endOfDay(customEndDate) }
            default:
                return { start: startOfDay(now), end: endOfDay(now) }
        }
    }, [periodFilter, customStartDate, customEndDate])

    // Filtrar bookings
    const filteredBookings = useMemo(() => {
        return bookings.filter(booking => {
            // Filtro de período
            const bookingDate = booking.createdAt?.toDate?.() || new Date()
            if (bookingDate < dateRange.start || bookingDate > dateRange.end) return false

            // Filtro de gateway
            if (gatewayFilter !== 'all') {
                const provider = booking.payment?.provider || 'pix_manual'
                if (provider !== gatewayFilter) return false
            }

            // Filtro de status
            if (statusFilter !== 'all') {
                const status = booking.payment?.status || 'pending'
                if (status !== statusFilter) return false
            }

            return true
        })
    }, [bookings, dateRange, gatewayFilter, statusFilter])

    // Calcular estatísticas
    const stats = useMemo(() => {
        const total = filteredBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0)
        const received = filteredBookings
            .filter(b => b.payment?.status === 'approved' || b.status === 'CONFIRMED')
            .reduce((sum, b) => sum + (b.totalPrice || 0), 0)
        const pending = filteredBookings
            .filter(b => b.payment?.status === 'pending' || b.status === 'PENDING_PAYMENT')
            .reduce((sum, b) => sum + (b.totalPrice || 0), 0)

        // Por gateway
        const byGateway = {
            mercadopago: filteredBookings.filter(b => b.payment?.provider === 'mercadopago'),
            infinitypay: filteredBookings.filter(b => b.payment?.provider === 'infinitypay'),
            pix_manual: filteredBookings.filter(b => b.payment?.provider === 'pix_manual' || !b.payment?.provider)
        }

        return {
            totalBookings: filteredBookings.length,
            totalRevenue: total,
            totalReceived: received,
            totalPending: pending,
            byGateway: {
                mercadopago: {
                    count: byGateway.mercadopago.length,
                    amount: byGateway.mercadopago.reduce((sum, b) => sum + (b.totalPrice || 0), 0)
                },
                infinitypay: {
                    count: byGateway.infinitypay.length,
                    amount: byGateway.infinitypay.reduce((sum, b) => sum + (b.totalPrice || 0), 0)
                },
                pix_manual: {
                    count: byGateway.pix_manual.length,
                    amount: byGateway.pix_manual.reduce((sum, b) => sum + (b.totalPrice || 0), 0)
                }
            }
        }
    }, [filteredBookings])

    // Exportar para CSV
    const exportToCSV = () => {
        const headers = ['Data', 'Membro', 'Espaço', 'Valor', 'Gateway', 'Método', 'Status', 'ID Transação']
        const rows = filteredBookings.map(b => [
            format(b.createdAt?.toDate?.() || new Date(), 'dd/MM/yyyy HH:mm'),
            (b as any).userName || 'N/A',
            (b as any).spaceName || 'N/A',
            `R$ ${b.totalPrice?.toFixed(2)}`,
            b.payment?.provider || 'pix_manual',
            b.payment?.method || 'pix',
            b.payment?.status || 'pending',
            b.payment?.mercadoPagoPaymentId || b.payment?.infinityPayChargeId || 'N/A'
        ])

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `relatorio-financeiro-${format(new Date(), 'dd-MM-yyyy')}.csv`
        link.click()
        toast.success('Relatório exportado!')
    }

    if (loading) {
        return (
            <div className="py-20 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            </div>
        )
    }

    return (
        <div className="p-6 md:p-12 space-y-8 page-enter">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 pb-8 border-b-2 border-primary/5">
                <div className="space-y-2">
                    <h1 className="text-4xl font-black text-primary tracking-tighter uppercase italic leading-tight">
                        Dashboard Financeiro
                    </h1>
                    <p className="text-gray-600 text-sm">
                        Gestão completa de receitas e fechamento de caixa
                    </p>
                </div>
                <button
                    onClick={exportToCSV}
                    className="btn-primary flex items-center gap-2 px-6 py-3"
                >
                    <Download className="w-5 h-5" />
                    Exportar CSV
                </button>
            </div>

            {/* Filtros */}
            <div className="glass-card p-6 space-y-4">
                <div className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <Filter className="w-4 h-4" />
                    Filtros
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Período */}
                    <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2">Período</label>
                        <select
                            value={periodFilter}
                            onChange={(e) => setPeriodFilter(e.target.value as PeriodFilter)}
                            className="input input-primary w-full text-sm"
                        >
                            <option value="today">Hoje</option>
                            <option value="week">Esta Semana</option>
                            <option value="month">Este Mês</option>
                            <option value="custom">Personalizado</option>
                        </select>
                    </div>

                    {/* Gateway */}
                    <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2">Gateway</label>
                        <select
                            value={gatewayFilter}
                            onChange={(e) => setGatewayFilter(e.target.value as GatewayFilter)}
                            className="input input-primary w-full text-sm"
                        >
                            <option value="all">Todos</option>
                            <option value="mercadopago">Mercado Pago</option>
                            <option value="infinitypay">InfinityPay</option>
                            <option value="pix_manual">PIX Manual</option>
                        </select>
                    </div>

                    {/* Status */}
                    <div>
                        <label className="block text-xs font-bold text-gray-600 mb-2">Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                            className="input input-primary w-full text-sm"
                        >
                            <option value="all">Todos</option>
                            <option value="pending">Pendente</option>
                            <option value="approved">Aprovado</option>
                            <option value="rejected">Rejeitado</option>
                        </select>
                    </div>

                    {/* Limpar */}
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setPeriodFilter('today')
                                setGatewayFilter('all')
                                setStatusFilter('all')
                            }}
                            className="w-full px-4 py-2 text-sm font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                        >
                            Limpar Filtros
                        </button>
                    </div>
                </div>
            </div>

            {/* Cards de Resumo */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Total de Reservas */}
                <div className="glass-card p-6 space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Reservas</p>
                        <Calendar className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-3xl font-black text-gray-900">{stats.totalBookings}</p>
                    <p className="text-xs text-gray-500">No período selecionado</p>
                </div>

                {/* Total a Receber */}
                <div className="glass-card p-6 space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">A Receber</p>
                        <DollarSign className="w-5 h-5 text-purple-500" />
                    </div>
                    <p className="text-3xl font-black text-gray-900">R$ {stats.totalRevenue.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Total do período</p>
                </div>

                {/* Total Recebido */}
                <div className="glass-card p-6 space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Recebido</p>
                        <TrendingUp className="w-5 h-5 text-green-500" />
                    </div>
                    <p className="text-3xl font-black text-green-600">R$ {stats.totalReceived.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Pagamentos confirmados</p>
                </div>

                {/* Total Pendente */}
                <div className="glass-card p-6 space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Pendente</p>
                        <CreditCard className="w-5 h-5 text-yellow-500" />
                    </div>
                    <p className="text-3xl font-black text-yellow-600">R$ {stats.totalPending.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Aguardando confirmação</p>
                </div>
            </div>

            {/* Análise por Gateway */}
            <div className="glass-card p-6 space-y-4">
                <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Por Gateway</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-xl">
                        <p className="text-xs font-bold text-blue-600 uppercase mb-2">Mercado Pago</p>
                        <p className="text-2xl font-black text-blue-900">R$ {stats.byGateway.mercadopago.amount.toFixed(2)}</p>
                        <p className="text-xs text-blue-700 mt-1">{stats.byGateway.mercadopago.count} transações</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-xl">
                        <p className="text-xs font-bold text-purple-600 uppercase mb-2">InfinityPay</p>
                        <p className="text-2xl font-black text-purple-900">R$ {stats.byGateway.infinitypay.amount.toFixed(2)}</p>
                        <p className="text-xs text-purple-700 mt-1">{stats.byGateway.infinitypay.count} transações</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-xs font-bold text-gray-600 uppercase mb-2">PIX Manual</p>
                        <p className="text-2xl font-black text-gray-900">R$ {stats.byGateway.pix_manual.amount.toFixed(2)}</p>
                        <p className="text-xs text-gray-700 mt-1">{stats.byGateway.pix_manual.count} transações</p>
                    </div>
                </div>
            </div>

            {/* Tabela de Transações */}
            <div className="glass-card p-6 space-y-4">
                <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">
                    Transações Detalhadas ({filteredBookings.length})
                </h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4 font-bold text-gray-600 uppercase text-xs">Data/Hora</th>
                                <th className="text-left py-3 px-4 font-bold text-gray-600 uppercase text-xs">Membro</th>
                                <th className="text-left py-3 px-4 font-bold text-gray-600 uppercase text-xs">Espaço</th>
                                <th className="text-right py-3 px-4 font-bold text-gray-600 uppercase text-xs">Valor</th>
                                <th className="text-left py-3 px-4 font-bold text-gray-600 uppercase text-xs">Gateway</th>
                                <th className="text-left py-3 px-4 font-bold text-gray-600 uppercase text-xs">Método</th>
                                <th className="text-left py-3 px-4 font-bold text-gray-600 uppercase text-xs">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBookings.map((booking) => {
                                const date = booking.createdAt?.toDate?.() || new Date()
                                const provider = booking.payment?.provider || 'pix_manual'
                                const method = booking.payment?.method || 'pix'
                                const status = booking.payment?.status || 'pending'

                                return (
                                    <tr key={booking.id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-3 px-4 text-gray-900 font-medium">
                                            {format(date, 'dd/MM/yyyy HH:mm')}
                                        </td>
                                        <td className="py-3 px-4 text-gray-900">{(booking as any).userName || 'N/A'}</td>
                                        <td className="py-3 px-4 text-gray-900">{(booking as any).spaceName || 'N/A'}</td>
                                        <td className="py-3 px-4 text-right font-bold text-gray-900">
                                            R$ {booking.totalPrice?.toFixed(2)}
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                                                {provider === 'mercadopago' ? 'MP' : provider === 'infinitypay' ? 'IP' : 'PIX'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-gray-600 capitalize">{method.replace('_', ' ')}</td>
                                        <td className="py-3 px-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${status === 'approved' ? 'bg-green-100 text-green-800' :
                                                status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-red-100 text-red-800'
                                                }`}>
                                                {status === 'approved' ? 'Aprovado' : status === 'pending' ? 'Pendente' : 'Rejeitado'}
                                            </span>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    {filteredBookings.length === 0 && (
                        <div className="py-12 text-center text-gray-500">
                            Nenhuma transação encontrada para os filtros selecionados
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
